import { DOI_URL } from "constants/config";
import { useFullPaperLibraryLink } from "features/libraries";
import useUserSettings from "features/User/hooks/useUserSettings";
import { Paper, PaperDetails } from "helpers/api";
import { addUTMToLink } from "helpers/pageUrl";

export type PdfAccessOption =
  | "uploaded"
  | "library"
  | "open-access"
  | "doi"
  | null;

export interface PdfAccessInfo {
  option: PdfAccessOption;
  url: string | undefined;
  label: string;
  icon: string | React.ReactElement;
}

export function usePdfAccessHierarchy(
  paper: Paper | PaperDetails,
  fullTextLabel: string = "Full text",
  pdfLabel: string = "PDF",
  isUserUploadedPaper: boolean = false
): PdfAccessInfo {
  const { library_id } = useUserSettings();

  // Get LibKey access if available
  const { data: fullPaperLibraryLink } = useFullPaperLibraryLink(
    library_id,
    paper.doi,
    Boolean(library_id && paper.doi)
  );

  // Check if this is a PaperDetails object (has open_access_pdf_url)
  const isPaperDetails = "open_access_pdf_url" in paper;
  const openAccessUrl = isPaperDetails
    ? (paper as PaperDetails).open_access_pdf_url
    : undefined;

  // Determine the best available option (Uploaded → LibKey → Open Access → DOI)
  let option: PdfAccessOption = null;
  let url: string | undefined;
  let label: string = fullTextLabel;
  let icon: string | React.ReactElement = "icon-external-link";

  if (isUserUploadedPaper) {
    option = "uploaded";
    // For uploaded papers, we don't set a URL here as it's handled by ManagePdfButton
    url = undefined;
    label = "Manage PDF";
    icon = "icon-file-check";
  } else if (fullPaperLibraryLink) {
    option = "library";
    url = fullPaperLibraryLink;
    label = pdfLabel;
    icon = "icon-library"; // We'll need to import LibraryIcon in components that use this
  } else if (openAccessUrl) {
    option = "open-access";
    url = openAccessUrl;
    label = pdfLabel;
    icon = "/icons/open-access.svg";
  } else if (paper.doi) {
    option = "doi";
    url = `${DOI_URL}/${paper.doi}`;
    label = fullTextLabel;
    icon = "icon-external-link";
  }

  return {
    option,
    url: addUTMToLink(url),
    label,
    icon,
  };
}
